import {Component, h, Host, Prop, Event, Element} from '@stencil/core';
import type {EventEmitter} from '@stencil/core';
import { makeLogger } from '../../../logger/logger';

const { err } = makeLogger({
  pathToCurrentFile: 'components/widgets/cta-widget/cta-widget.tsx',
  loggersList: ['err']
});

type ClickType = 'left'|'right'|'keyboard'|'Enter';

export type CtaWidgetClickEvent = {
  category:string
  tabName: string,
  redirectionUrl:string
  clickType:ClickType
  term: string
  ctaName?: string
  ctaLocation?: string
};

@Component({
  tag: 'uiaas-search-cta-widget-v2',
  styleUrl: 'cta-widget-v2.scss',
  shadow: true,
})
export class CtaWidgetv2 {

  @Event({
    eventName: 'uiaas:widgetLoaded',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    loadEventEmitter:EventEmitter;


  @Event({
    eventName: 'search:ctaWidgetv2Event',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    statisticsEventEmitter: EventEmitter<CtaWidgetClickEvent>;

  /**
   * Title displayed on top
   */
  @Prop() readonly header: string;

  /**
   * Text displayed in the middle of widget
   */
  @Prop() readonly content: string;

  /**
   * QDS3 icon name
   */
  @Prop() readonly icon: string;

  /**
   * href attribute of an anchor
   */
  @Prop() readonly url: string;

  /**
   * value used in GA4 events. Should be unique for every type of event
   */
  @Prop() readonly eventCategory: string;

  /**
   * target attribute of an anchor. Allows to open link in new tab
   */
  @Prop() readonly anchorTarget: string = '';

  /**
   * Value used in GA4 events. By the default component uses parent's tabName. Using this property will override it
   */
  @Prop() readonly tabName: string;

  /**
   * Text for redirect button
   */
  @Prop() readonly buttonText: string;

  /**
   * Text for redirect button
   */
  @Prop() readonly extendedButton = false;

  @Element() el: HTMLUiaasSearchCtaWidgetElement;

  private computedTab: string = '';
  private query: string = '';


  connectedCallback() {
      const closestTabEl = this.el.closest('uiaas-search-tab,dashboard-tab-view-v2');
      this.computedTab = this.tabName || closestTabEl?.['tabName'];
      const uiaasSearchElement = document.querySelector('uiaas-search');
      if (uiaasSearchElement) {
        this.query = uiaasSearchElement.getAttribute('query');
      }
      this.loadEventEmitter.emit();
  }

  private emitSearchStatisticsEvent = (clickType:ClickType, redirectionUrl:string) => {
      this.statisticsEventEmitter.emit({
        category: this.eventCategory,
        tabName: this.computedTab,
        redirectionUrl,
        clickType,
        term: this.query
      });
  };

  private getButtonText(){
      let text = this.buttonText;
      if(text.length > 24)
      {
        text = `${text.slice(0, 24)}...`;
      }
      return text;
  }

  private renderButton() {
      return <qds-button
        size="large"
        class="cta-widget-v2-button"
        slot="trigger"
        href={this.url}
        target={this.anchorTarget}
        text={this.getButtonText()}
        onMouseDown={e => {this.emitSearchStatisticsEvent(e.button === 0 ? 'left' : 'right', this.url);}}
        onKeyDown={e => {e.key === 'Enter' && this.emitSearchStatisticsEvent(e.key, this.url);}}
      />;
  }

  private renderTooltip(){
      return <se-tooltip color="alternative" class="cta-widget-v2-button">
        {this.renderButton()}
        <se-tooltip-content>{this.buttonText}</se-tooltip-content>
      </se-tooltip>;
  }

  render() {
      return <qds-web-card
        appearance="outline"
        importance="standard"
        layout="grid"
        hover="no"
        footer-slot="no"
        focus-ring="false"
        media="false"
        class="cta-widget-v2"
      >
        <slot name="icon">
          {this.icon}
        </slot>
        <div class="cta-widget-v2-header-container">
          <div class="cta-widget-v2-header-title" >
            <span class="cta-widget-v2-header-content">{this.header}</span>
            <p class="cta-widget-v2-content-description">
              {this.content}
            </p>
          </div>
        </div>
        <div class={{'cta-widget-v2-button-container': true, 'extended': this.extendedButton}}>
          {(this.buttonText?.length > 24 && !this.extendedButton) ? this.renderTooltip() : this.renderButton()}
        </div>
      </qds-web-card>;
  }
}

